import { clearLegFeedback, setLegLoading } from '@/Board/dispatchBoardSlice';
import { parseIsoDate } from '@api/Trinium/types';
import { store } from '@reduxStore';
import { legToDispatchLeg, legToDispatchLegOptimistic } from '../../maps';
import { triniumApi } from '../../slice';
import { consignmentSlice } from '../Consignment/slice';
import { removeLegInDispatchCache, upsertLegInDispatchCache, upsertLegsInDispatchCache } from '../Dispatch/types';
import {
	Leg,
	DeleteLegRequest,
	LegRaw,
	PutLegAssignResourcesRawResponse,
	PutLegRequest,
	PutLegResponse,
	PutLegsRawResponse,
	PutLegsResponse,
	PutLegsRequest,
	PatchLegsRequest,
	PatchLegsResponse,
	PatchLegsRawResponse,
	transformLegResponse,
	transformLegsResponse,
	updateLegApiCache,
	updateLegConsignmentCache,
	removeLegApiCache,
	PatchLegRequest,
	SetScheduleRequest
} from './types';

export const legSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchLeg: builder.query<Leg, { terminalNumber: string; id: number }>({
				query: (params) => `${params.terminalNumber}/legs/${String(params.id)}`,
				transformResponse: transformLegResponse,
				providesTags: (response) => (response ? [{ type: 'legs', id: response.id }] : []),
				async onQueryStarted(_params, { queryFulfilled }) {
					const result = await queryFulfilled;

					// Update the cache with the newly retrieved Leg object
					upsertLegInDispatchCache(legToDispatchLeg(result.data));
				}
			}),

			patchLeg: builder.mutation<Leg[], PatchLegRequest>({
				query: ({ terminalNumber, leg: { id, ...legDetails } }) => ({
					url: `${terminalNumber}/legs/${String(id)}`,
					method: 'PATCH',
					body: {
						...legDetails
					}
				}),
				transformResponse: transformLegsResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					/** After updating to redux 2.0, PatchCollection type doesn't seem to be exposed */
					let optimisticUpdates = undefined;
					if (params.optimistic) {
						optimisticUpdates = upsertLegInDispatchCache(legToDispatchLegOptimistic(params.leg, params.optimisticExtensions));
					}

					try {
						const result = await queryFulfilled;

					// When the actual result is returned, update cache with all legs returned
					result.data.forEach((leg) => {
						upsertLegInDispatchCache(legToDispatchLeg(leg));
						if (!params.skipCacheUpdate) {
							updateLegApiCache(leg);
						}
					});

					/** Ensure we have at least one leg and a consignment id */
					if (!params.skipCacheUpdate && result.data.length > 0 && result.data[0].csnId) {
							const refreshedLegs = [...result.data].sort((a, b) => {
								if (a.no > b.no) {
									return 1;
								}
								return -1;
							});

							/** Update the Consignment Cache with the returned collection of legs */
							dispatch(
								consignmentSlice.util.updateQueryData(
									'fetchConsignment',
									{
										terminalNumber: params.terminalNumber,
										consignmentId: result.data[0].csnId
									},
									(draft) => {
										Object.assign(draft, {
											// If any Consignment values were changed in this request, copy them, otherwise retain the values
											cntr: {
												...draft.cntr,
												sealNo: params.leg.puSealNo ?? draft.cntr?.sealNo,
												emptyRelNo: params.leg.relNo ?? draft.cntr?.emptyRelNo
											},
											cntrId: params.leg.cntrId !== undefined ? params.leg.cntrId : draft.cntrId
										});
										draft.consignmentLegs.splice(0, draft.consignmentLegs.length, ...refreshedLegs);
									}
								)
							);
						}
					} catch {
						// Rollback the optimistic update and forward on the error
						optimisticUpdates?.forEach((currentOptimisticUpdate) => {
							currentOptimisticUpdate.undo();
						});
					}
				}
			}),

			patchLegs: builder.mutation<PatchLegsResponse, PatchLegsRequest>({
				query: ({ terminalNumber, legIds, leg }) => ({
					url: `${terminalNumber}/legs`,
					method: 'PUT',
					body: {
						action: 'updateLegs',
						data: {
							legIds,
							...leg
						}
					}
				}),
				transformResponse: (result: PatchLegsRawResponse): PatchLegsResponse => ({
					legs: transformLegsResponse(result.legs ?? []),
					errors: result.errors ?? []
				}),
				async onQueryStarted(params, { queryFulfilled }) {
					store.dispatch(setLegLoading(params.legIds));

					try {
						const result = await queryFulfilled;
						upsertLegsInDispatchCache(result.data.legs.map(legToDispatchLeg));
						result.data.legs.forEach((leg) => {
							updateLegApiCache(leg);
							updateLegConsignmentCache(leg);
						});
					} finally {
						store.dispatch(clearLegFeedback(params.legIds));
					}
				}
			}),

			putLegs: builder.mutation<PutLegsResponse, PutLegsRequest>({
				query: (params) => ({
					url: `${params.terminalNumber}/legs`,
					method: 'PUT',
					body: {
						action: params.actionType.action,
						data: params.actionType.data
					}
				}),
				/** Hubbing still returns a raw `LegRaw[]`; the batch actions return `{ legs, errors }` with either collection omissible */
				transformResponse: (result: LegRaw[] | PutLegsRawResponse): PutLegsResponse =>
					Array.isArray(result)
						? { legs: transformLegsResponse(result), errors: [] }
						: { legs: transformLegsResponse(result.legs ?? []), errors: result.errors ?? [] },
				async onQueryStarted(params, { queryFulfilled }) {
					const { legIds } = params.actionType.data;
					store.dispatch(setLegLoading(legIds));

					try {
						const result = await queryFulfilled;
						/** Hubbing returns the source leg alongside the leg it just created, which no cache tag can locate yet */
						upsertLegsInDispatchCache(result.data.legs.map(legToDispatchLeg), {
							isNewLeg: params.actionType.action === 'hubContainer'
						});
						result.data.legs.forEach((leg) => {
							updateLegApiCache(leg);
							updateLegConsignmentCache(leg);
						});
					} finally {
						store.dispatch(clearLegFeedback(legIds));
					}
				},
				invalidatesTags: (response, _error, args) => {
					const invalidatedTags: ({ type: 'drivers' | 'tractors' } | { type: 'consignment'; id: number })[] = [];

					/** Mirrors putLeg: re-establish the driver/tractor mapping after an assignment */
					if (args.actionType.action === 'assignResources') {
						invalidatedTags.push({ type: 'drivers' }, { type: 'tractors' });
					}

					/**
					 * Unlike the single-leg PUT, the batch response carries no canApprove flag, so refetch each affected
					 * consignment to keep the Approve button in step with the driver assignment change.
					 */
					if (args.actionType.action === 'assignResources' || args.actionType.action === 'undo') {
						const csnIds = new Set(response?.legs.map((leg) => leg.csnId).filter((csnId): csnId is number => csnId !== undefined));
						csnIds.forEach((csnId) => invalidatedTags.push({ type: 'consignment', id: csnId }));
					}

					return invalidatedTags;
				}
			}),

			putLeg: builder.mutation<PutLegResponse, PutLegRequest>({
				query: (params) => ({
					url: `${params.terminalNumber}/legs/${String(params.id)}`,
					method: 'PUT',
					body: {
						action: params.actionType.action,
						data: params.actionType.data
							? {
									...params.actionType.data
								}
							: undefined
					}
				}),
				/**
				 * The assignResources action returns `{ legs, canApprove }`; all other PUT actions return a raw `LegRaw[]`.
				 * Normalize both shapes to PutLegResponse so callers and onQueryStarted can read `result.data.legs`.
				 */
				transformResponse: (result: LegRaw[] | PutLegAssignResourcesRawResponse): PutLegResponse => {
					if (Array.isArray(result)) {
						return { legs: transformLegsResponse(result) };
					}
					return { legs: transformLegsResponse(result.legs), canApprove: result.canApprove };
				},
				async onQueryStarted(params, { queryFulfilled }) {
					const result = await queryFulfilled;

					// When the actual result is returned, update cache with all legs returned
					result.data.legs.forEach((leg) => {
						upsertLegInDispatchCache(legToDispatchLeg(leg));
						if (!params.skipCacheUpdate) {
							updateLegApiCache(leg);
							updateLegConsignmentCache(leg);
						}
					});

					/**
					 * When an action that affects driver/tractor assignment (assignResources, undo) returns a
					 * canApprove flag, patch the consignment cache so the Approve button enables/disables without
					 * requiring a refresh. Keyed off the response shape so any future action that wraps canApprove
					 * picks this up automatically. Skipped when skipCacheUpdate is true so the batched callers in
					 * sendLegMutations can apply this once at the end.
					 */
					if (!params.skipCacheUpdate && result.data.canApprove !== undefined) {
						const csnId = result.data.legs[0]?.csnId;
						if (csnId) {
							store.dispatch(
								consignmentSlice.util.updateQueryData(
									'fetchConsignment',
									{ consignmentId: csnId, terminalNumber: params.terminalNumber },
									(draft) => {
										draft.canApprove = result.data.canApprove;
									}
								)
							);
						}
					}
				},
				invalidatesTags: (_, __, args) => {
					const invalidatedTags = [];
					/** Invalidate the Driver and Tractors to re-establish mapping after as assignment */
					if (args.actionType.action === 'assignResources') {
						invalidatedTags.push({ type: 'drivers' as const });
						invalidatedTags.push({ type: 'tractors' as const });
					}

					return invalidatedTags;
				}
			}),
			/**
			 * RTK Endpoint to delete a Leg. The Leg will be manually removed from the Dispatch API and the parent Consignment API.
			 * When the subscription to the Fetch Leg API expires than it will be removed from that cache as well.
			 */
			deleteLeg: builder.mutation<Leg[], DeleteLegRequest>({
				query: (params) => ({
					url: `${params.terminalNumber}/legs/${String(params.id)}`,
					method: 'DELETE',
					...(params.deleteDriverPayments !== undefined && { params: { deleteDriverPayments: params.deleteDriverPayments } })
				}),
				// Answering the driver pay question deletes the leg's pay or keeps it on the order without a leg, so the Payments grid is stale too
				invalidatesTags: (_response, _errors, params) =>
					params.csnId
						? [
								{ type: 'consignment', id: params.csnId },
								...(params.deleteDriverPayments !== undefined ? [{ type: 'payments' as const, id: params.csnId }] : [])
							]
						: [],
				transformResponse: transformLegsResponse,
				async onQueryStarted(params, { dispatch, queryFulfilled }) {
					const result = await queryFulfilled;

					// Update the cache with the returned collection of legs
					result.data.forEach((leg) => {
						upsertLegInDispatchCache(legToDispatchLeg(leg));
						updateLegApiCache(leg);
					});

					/** Ensure we have at least one leg and a consignment id */
					if (result.data[0]?.csnId) {
						const refreshedLegs = [...result.data].sort((a, b) => {
							if (a.no > b.no) {
								return 1;
							}
							return -1;
						});

						/** Update the Consignment Cache with the returned collection of legs */
						dispatch(
							consignmentSlice.util.updateQueryData(
								'fetchConsignment',
								{
									terminalNumber: params.terminalNumber,
									consignmentId: result.data[0].csnId
								},
								(draft) => {
									Object.assign(draft, {
										// Retain existing consignment values
										sealNo: draft.cntr?.sealNo,
										cntrId: draft.cntrId
									});
									draft.consignmentLegs.splice(0, draft.consignmentLegs.length, ...refreshedLegs);
								}
							)
						);
					}

					/** Remove the Leg from the Dispatch and API cache */
					removeLegInDispatchCache({
						legId: params.id,
						jobId: params.jobId
					});
					removeLegApiCache(params.id);
				}
			}),

			setSchedule: builder.mutation<null, SetScheduleRequest>({
				query: (params) => ({
					url: `${params.terminalNumber}/legs`,
					method: 'PUT',
					body: {
						action: 'setSchedule',
						data: params.actionType.data
					}
				}),
				async onQueryStarted(params, { queryFulfilled }) {
					const { legIds, ...legData } = params.actionType.data;
					store.dispatch(setLegLoading(legIds));

					try {
						// api returns an empty 200 response for 'setSchedule' action - params data used to update cache
						await queryFulfilled;
						const updatedLegData = {
							...(legData.schdPuFrom && {
								scheduledPickupDateFrom: parseIsoDate(legData.schdPuFrom)
							}),
							...(legData.schdPuTo && {
								scheduledPickupDateTo: parseIsoDate(legData.schdPuTo)
							}),
							...(legData.schdDelvFrom && {
								scheduledDeliveryDateFrom: parseIsoDate(legData.schdDelvFrom)
							}),
							...(legData.schdDelvTo && {
								scheduledDeliveryDateTo: parseIsoDate(legData.schdDelvTo)
							}),
							...(legData.removeSchdPuDate && {
								scheduledPickupDateFrom: undefined,
								scheduledPickupDateTo: undefined
							}),
							...(legData.removeSchdDelvDate && {
								scheduledDeliveryDateFrom: undefined,
								scheduledDeliveryDateTo: undefined
							})
						};

						legIds.forEach((legId) => {
							const dispatchLeg = {
								legId,
								...updatedLegData
							};

							upsertLegInDispatchCache(dispatchLeg);
						});
					} finally {
						store.dispatch(clearLegFeedback(legIds));
					}
				}
			})
		};
	}
});

export const {
	useFetchLegQuery,
	useLazyFetchLegQuery,
	usePatchLegMutation,
	usePatchLegsMutation,
	usePutLegMutation,
	usePutLegsMutation,
	useDeleteLegMutation,
	useSetScheduleMutation
} = legSlice;
